import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Screen } from '../../app/Frame';
import { useMeContext } from '../../app/MeContext';
import { useScreen, useToast } from '../../app/shell-context';
import { ApiError } from '../../api/client';
import { useBooking, useBookingGuard } from '../BookingContext';
import { useSaveContact } from '../api';
import { HoldTimer } from '../HoldTimer';

/** Шаг 4 из 5: анкета. Телефон обязателен — по нему звонят, когда связи в Telegram нет. */
export function Form() {
  const me = useMeContext();
  const navigate = useNavigate();
  const toast = useToast();
  const { booking: raw, patch } = useBooking();
  const booking = useBookingGuard((b) => b.appointmentId !== null);
  const saveContact = useSaveContact();

  const child = (me.children ?? []).find((c) => c.id === raw.childId);
  const [name, setName] = useState(raw.intake.name || child?.name || '');
  const [age, setAge] = useState(raw.intake.age || ageOf(child?.birth_year));
  const [note, setNote] = useState(raw.intake.note || child?.note || '');
  const [phone, setPhone] = useState(raw.intake.phone || me.client?.phone || '');
  const [consent, setConsent] = useState(raw.intake.consent || !!me.client?.hasConsent);

  const digits = phone.replace(/\D/g, '');
  const ready = name.trim().length > 1 && digits.length >= 10 && consent;

  useEffect(() => {
    patch({ intake: { name, age, note, phone, consent } });
  }, [name, age, note, phone, consent]); // eslint-disable-line react-hooks/exhaustive-deps

  useScreen('О ребёнке', {
    label: saveContact.isPending ? 'Секунду…' : 'Далее',
    disabled: !ready || saveContact.isPending,
    onClick: () => next(),
  });

  if (!booking) return null;

  function next() {
    const phoneChanged = phone.trim() !== (me.client?.phone ?? '');
    // Телефон и согласие уходят на сервер один раз: без них запись всё равно
    // не состоится — сервер ответит CONTACT_REQUIRED.
    if (!phoneChanged && me.client?.hasConsent) {
      navigate('/confirm');
      return;
    }
    saveContact.mutate(
      { phone: phone.trim(), source: 'form', consent: true, firstName: me.client?.firstName },
      {
        onSuccess: () => navigate('/confirm'),
        onError: (e) => toast(e instanceof ApiError ? e.message : 'Не получилось сохранить телефон'),
      },
    );
  }

  return (
    <Screen>
      <div>
        <p className="eyebrow">Шаг 4 из 5</p>
        <p className="lead" style={{ marginTop: 6 }}>
          Коротко о ребёнке — чтобы специалист подготовился к встрече.
        </p>
      </div>

      <HoldTimer />

      <div className="two">
        <div className="field">
          <label htmlFor="fName">Имя ребёнка</label>
          <input id="fName" type="text" placeholder="Матвей" autoComplete="off" value={name} onChange={(e) => setName(e.target.value)} />
        </div>
        <div className="field">
          <label htmlFor="fAge">Возраст</label>
          <input id="fAge" type="number" min="3" max="17" placeholder="7" inputMode="numeric" value={age} onChange={(e) => setAge(e.target.value)} />
        </div>
      </div>

      <div className="field">
        <label htmlFor="fReq">С чем пришли</label>
        <textarea
          id="fReq"
          placeholder="Например: трудно удерживать внимание на уроках, жалобы от учителя"
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />
        <span className="hint">Свободная форма, пары предложений достаточно.</span>
      </div>

      <div className="field">
        <label htmlFor="fPhone">Телефон для связи</label>
        <input id="fPhone" type="tel" placeholder="+7 900 000-00-00" autoComplete="tel" value={phone} onChange={(e) => setPhone(e.target.value)} />
        <span className="hint">
          Обязательно. Сообщения о переносах бот пришлёт в Telegram, но если организации придётся отменить
          запись срочно — позвонят.
        </span>
      </div>

      <label className="check">
        <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} />
        <span>Согласен на обработку персональных данных</span>
      </label>
    </Screen>
  );
}

function ageOf(birthYear: number | null | undefined): string {
  if (!birthYear) return '';
  return String(new Date().getUTCFullYear() - birthYear);
}
